import { DatePipe } from '@angular/common';
import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { CheckoutHotelSpecialRequestsIn, SpecialRequestType } from '@appTypes/api.types';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { DynamicModalService } from '@shared/modules/dynamic-modal/dynamic-modal.service';
import { take } from 'rxjs';
import {
  SpecialRequestsApiService,
  SpecialRequestsError,
  toSpecialRequestsError
} from './special-requests-api.service';
import { focusRoomLine } from './special-requests-focus';
import {
  SpecialRequestsCard,
  SpecialRequestsPopupComponent,
  SpecialRequestsPopupData,
  SpecialRequestsPopupResult,
  specialRequestsDialog
} from './special-requests-popup.component';
import {
  SpecialRequestsDraft,
  SpecialRequestsOtherRoom,
  draftHasContent,
  emptyDraft,
  itemName,
  nightsBetween,
  summarize
} from './special-requests.util';

/** One room of the checkout cart as the draft service needs it (a hotel line of `CheckoutService.hotelsData`). */
export interface SpecialRequestsCheckoutRoom {
  _formIndex: number;
  hotelName?: string | null;
  hotelClass?: string | null;
  mealPlan?: string | null;
  nights?: number | null;
  hotelPriceId?: string | null;
  roomCategoryName?: string | null;
  placementTypeName?: string | null;
  placementDescription?: string | null;
  checkInDate?: string | null;
  checkOutDate?: string | null;
}

export type SpecialRequestsTypesState =
  | { kind: 'loading' }
  | { kind: 'ready'; types: SpecialRequestType[] }
  | { kind: 'unavailable'; error: SpecialRequestsError }
  /** R-SR45: flag `booking-special-requests` Off - no line, no box */
  | { kind: 'hidden' };

/**
 * Checkout side of the booking special requests (QA doc docs features/booking-special-requests/qa.md R-SR6 .. R-SR13): loads the
 * operator's list once per checkout, keeps one draft per cart room (by the hotel form index) while the agent fills the page, opens
 * the popup, and turns the drafts into the `specialRequests` of each `CheckoutHotelIn` at **Register the reservation**. Nothing is
 * sent before that click (R-SR11). When the list cannot be read the checkout still registers (R-SR13: REQUEST_LIST_UNAVAILABLE box,
 * Register stays enabled). One instance per checkout visit: `CheckoutService.specialRequests` creates it on first use and the hotel
 * cart (`app-checkout-hotels`) provides that instance to the lines; `CheckoutService.destroy()` resets it.
 */
@Injectable()
export class SpecialRequestsDraftService {
  readonly #api = inject(SpecialRequestsApiService);
  readonly #modal = inject(DynamicModalService);
  readonly #translate = inject(TranslateService);
  readonly #injector = inject(Injector);
  readonly #date = new DatePipe('en-US');

  readonly state = signal<SpecialRequestsTypesState>({ kind: 'loading' });
  readonly drafts = signal<Record<number, SpecialRequestsDraft>>({});
  readonly lang = signal<string>(this.#translate.getCurrentLang() ?? '');
  readonly retrying = signal(false);
  /** F7: true while **Register the reservation** is on its way - the room lines are disabled, no popup opens. */
  readonly busy = signal(false);
  /** R-SR19f: the registration is on its way WITH special requests - **Register the reservation** reads "Saving…" + `aria-busy`. */
  readonly saving = computed(() => this.busy() && Object.values(this.drafts()).some((d) => draftHasContent(d)));
  #loaded = false;
  #opening = false;
  /** form index -> `hotelPriceId#n` of the room the draft was written for (see `sync`) */
  #keys: Record<number, string> = {};

  constructor() {
    this.#translate.onLangChange.subscribe((e) => this.lang.set(e.lang));
  }

  /** Reads the list once; a second call is a no-op until `retry()`. */
  load(): void {
    if (this.#loaded) return;
    this.#loaded = true;
    this.#fetch();
  }

  /** R-SR13: **Try again** on the unavailable box. */
  retry(): void {
    if (this.retrying()) return;
    this.retrying.set(true);
    this.#fetch();
  }

  #fetch(): void {
    if (!this.retrying()) this.state.set({ kind: 'loading' });
    this.#api.types().subscribe({
      next: (types) => {
        this.retrying.set(false);
        // R-SR45 / contract §1: `{ items: [] }` = flag Off or no active item - the section is not drawn
        this.state.set(
          types.length
            ? { kind: 'ready', types: [...types].sort((a, b) => a.sortOrder - b.sortOrder) }
            : { kind: 'hidden' }
        );
      },
      error: (e: unknown) => {
        this.retrying.set(false);
        const error = toSpecialRequestsError(e, 'REQUEST_LIST_UNAVAILABLE');
        this.state.set(error.code === 'FEATURE_FLAG_OFF' ? { kind: 'hidden' } : { kind: 'unavailable', error });
      }
    });
  }

  draft(formIndex: number): SpecialRequestsDraft | null {
    return this.drafts()[formIndex] ?? null;
  }

  hasContent(formIndex: number): boolean {
    return draftHasContent(this.draft(formIndex));
  }

  /** R-SR6 / R-SR11: the line under the room - items in the UI language, «wishes», the link. */
  summary(formIndex: number, rooms: SpecialRequestsCheckoutRoom[]): string {
    const d = this.draft(formIndex);
    const s = this.state();
    if (!d || !draftHasContent(d)) return '';
    const types = s.kind === 'ready' ? s.types : [];
    const lang = this.lang();
    const names = d.typeIds
      .map((id) => types.find((t) => t.typeId === id))
      .filter((t): t is SpecialRequestType => !!t)
      .map((t) => itemName(t, lang));
    let link: string | null = null;
    if (d.linkKind === 'room' && d.linkRoomKey !== null) {
      const idx = rooms.findIndex((r) => String(r._formIndex) === d.linkRoomKey);
      if (idx >= 0) link = this.#translate.instant('linked to Room {n}', { n: idx + 1 }) as string;
    }
    if (d.linkKind === 'booking' && d.bookingNumber.trim()) {
      link = this.#translate.instant('linked to reservation {n}', { n: d.bookingNumber.trim() }) as string;
    }
    return summarize(names, d.otherWishes, link);
  }

  /** Opens the popup for one cart room; **Save** keeps the draft on the page, **Cancel** / X leaves it as it was (R-SR6, R-SR7). */
  open(room: SpecialRequestsCheckoutRoom, rooms: SpecialRequestsCheckoutRoom[], opener?: HTMLElement | null): void {
    const s = this.state();
    if (s.kind !== 'ready' || this.#opening || this.busy()) return;
    this.#opening = true;
    const roomNo = rooms.findIndex((r) => r._formIndex === room._formIndex) + 1;
    const otherRooms: SpecialRequestsOtherRoom[] = rooms
      .map((r, i) => ({ r, roomNo: i + 1 }))
      .filter(({ r }) => r._formIndex !== room._formIndex)
      .map(({ r, roomNo: n }) => ({ key: String(r._formIndex), roomNo: n, label: r.hotelName ?? '' }));
    const data: SpecialRequestsPopupData = {
      mode: 'checkout',
      roomNo,
      roomLine: this.roomLine(room),
      card: this.card(room),
      types: s.types,
      draft: this.draft(room._formIndex) ?? emptyDraft(),
      otherRooms
    };
    const ref = this.#modal.open({
      component: SpecialRequestsPopupComponent,
      data,
      ...specialRequestsDialog(roomNo)
    });
    ref.closed.pipe(take(1)).subscribe((r: unknown) => {
      this.#opening = false;
      const result = r as SpecialRequestsPopupResult | undefined;
      if (result?.kind === 'draft') this.drafts.update((all) => ({ ...all, [room._formIndex]: result.draft }));
      // R-SDA16: Cancel, X, Escape, backdrop and Save all give focus back to the line the dialog came from
      focusRoomLine(opener, this.#injector);
    });
  }

  /** R-SDA3: the summary card at the top of the dialog - the same values as the cart row (name, stars, dates, nights, room · meal · accommodation). */
  card(room: SpecialRequestsCheckoutRoom): SpecialRequestsCard {
    const fmt = environment.formats.date;
    const from = room.checkInDate ? this.#date.transform(room.checkInDate, fmt) : null;
    const to = room.checkOutDate ? this.#date.transform(room.checkOutDate, fmt) : null;
    return {
      title: room.hotelName ?? '',
      stars: room.hotelClass?.trim() ? room.hotelClass.trim() : null,
      dates: from && to ? `${from} - ${to}` : (from ?? to),
      nights: room.nights && room.nights > 0 ? room.nights : nightsBetween(room.checkInDate, room.checkOutDate),
      meta: [
        room.roomCategoryName,
        room.mealPlan,
        room.placementTypeName?.trim() ? room.placementTypeName : room.placementDescription
      ].filter((x): x is string => !!x && !!String(x).trim())
    };
  }

  /** hotel · room · occupancy · period (prototype `pp-pop-room`); the parts come from the cart, never typed here. */
  roomLine(room: SpecialRequestsCheckoutRoom): string {
    const fmt = environment.formats.date;
    const from = room.checkInDate ? this.#date.transform(room.checkInDate, fmt) : null;
    const to = room.checkOutDate ? this.#date.transform(room.checkOutDate, fmt) : null;
    return [
      room.hotelName,
      room.roomCategoryName,
      room.placementTypeName?.trim() ? room.placementTypeName : room.placementDescription,
      from && to ? `${from} - ${to}` : (from ?? to)
    ]
      .filter((x): x is string => !!x && !!String(x).trim())
      .join(' · ');
  }

  /**
   * R-SR9 / R-SR11: the `specialRequests` of one hotel line at Register. A room link names the target's hotelPriceId; a link to a
   * room that left the cart is dropped (the rest of the request still goes). Undefined when the room has nothing.
   */
  toCheckoutIn(formIndex: number, rooms: SpecialRequestsCheckoutRoom[]): CheckoutHotelSpecialRequestsIn | undefined {
    const d = this.draft(formIndex);
    if (!d || !draftHasContent(d)) return undefined;
    const target = d.linkKind === 'room' ? rooms.find((r) => String(r._formIndex) === d.linkRoomKey) : undefined;
    const linkRoom = d.linkKind === 'room' && !!target?.hotelPriceId;
    const linkBooking = d.linkKind === 'booking' && !!d.bookingNumber.trim();
    let linkKind: CheckoutHotelSpecialRequestsIn['linkKind'] = null;
    if (linkRoom) linkKind = 'room';
    else if (linkBooking) linkKind = 'booking';
    const out: CheckoutHotelSpecialRequestsIn = {
      typeIds: [...d.typeIds],
      otherWishes: d.otherWishes.trim() || null,
      linkKind,
      linkHotelPriceId: linkRoom ? (target?.hotelPriceId ?? null) : null,
      linkBookingNumber: linkBooking ? d.bookingNumber.trim() : null
    };
    return out.typeIds.length || out.otherWishes || out.linkKind ? out : undefined;
  }

  /** A room removed from the cart takes its draft with it. */
  forget(formIndex: number): void {
    this.drafts.update((all) => {
      const next = { ...all };
      delete next[formIndex];
      return next;
    });
  }

  /**
   * The cart rooms changed (a hotel removed, the form rebuilt): the form indexes move, so every draft follows its room by
   * `hotelPriceId` (the n-th room with the same id = the n-th again). A draft whose room left the cart is dropped; a room link to a
   * room that left is dropped too (R-SR9: the rest of the request stays). A cart with a room without a hotelPriceId is left as is.
   */
  sync(rooms: SpecialRequestsCheckoutRoom[]): void {
    if (rooms.some((r) => !r.hotelPriceId)) return;
    const seen: Record<string, number> = {};
    const keys: Record<number, string> = {};
    for (const r of rooms) {
      const id = r.hotelPriceId as string;
      seen[id] = (seen[id] ?? 0) + 1;
      keys[r._formIndex] = `${id}#${seen[id]}`;
    }
    const before = this.#keys;
    this.#keys = keys;
    const drafts = this.drafts();
    // the first look at the cart only records the keys
    if (!Object.keys(before).length || !Object.keys(drafts).length) return;
    const indexOf: Record<string, number> = {};
    for (const [i, k] of Object.entries(keys)) indexOf[k] = Number(i);
    const moved = (oldIndex: number | string): number | undefined => {
      const k = before[Number(oldIndex)];
      return k === undefined ? undefined : indexOf[k];
    };
    const next: Record<number, SpecialRequestsDraft> = {};
    for (const [oldIndex, d] of Object.entries(drafts)) {
      const to = moved(oldIndex);
      if (to === undefined) continue;
      if (d.linkKind !== 'room' || d.linkRoomKey === null) {
        next[to] = d;
        continue;
      }
      const linkTo = moved(d.linkRoomKey);
      next[to] =
        linkTo === undefined ? { ...d, linkKind: 'none', linkRoomKey: null } : { ...d, linkRoomKey: String(linkTo) };
    }
    this.drafts.set(next);
  }

  /** The checkout was left: nothing of this visit is kept (a new checkout starts empty and reads the list again). */
  reset(): void {
    this.drafts.set({});
    this.state.set({ kind: 'loading' });
    this.retrying.set(false);
    this.busy.set(false);
    this.#loaded = false;
    this.#opening = false;
    this.#keys = {};
  }
}
